import { useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance';

const isUserAuthenticated = () => localStorage.getItem('isAuthenticated') === 'true';

export { isUserAuthenticated };

/**
 * Custom hook for fetching and caching workspace list
 * Uses React Query for automatic caching and refetching
 */
export const useWorkspaceList = (options = {}) => {
  const {
    enabled: enabledOption = true,
    page,
    pageSize,
    search = '',
  } = options;
  const enabled = enabledOption && isUserAuthenticated();
  const normalizedSearch = search.trim();

  return useQuery({
    queryKey: ['workspaces', { page, pageSize, search: normalizedSearch }],
    queryFn: async () => {
      if (!isUserAuthenticated()) {
        return {};
      }

      const params = {};

      if (page != null) {
        params.page = page;
      }
      if (pageSize != null) {
        params.page_size = pageSize;
      }
      if (normalizedSearch) {
        params.search = normalizedSearch;
      }

      const response = await axiosInstance.get('/workspace/list_workspaces', {
        params,
        paramsSerializer: (queryParams) =>
          Object.entries(queryParams)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&'),
      });
      return response || {};
    },
    enabled,
    // Cache workspaces — do not refetch every time the browser tab regains focus.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, error) =>
      error?.response?.status !== 401 && failureCount < 2,
  });
};

/**
 * Get recent workspaces (top 2)
 */
export const getRecentWorkspaces = (workspaces = []) => {
  return Array.isArray(workspaces) ? workspaces.slice(0, 2) : [];
};

/**
 * Add new workspace
 */
export const addWorkspace = async (workspaceName) => {
  try {
    const response = await axiosInstance.post(
      `/workspace/add_workspace?workspace_name=${encodeURIComponent(workspaceName)}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete workspace
 */
export const deleteWorkspace = async (workspaceId) => {
  try {
    const response = await axiosInstance.delete(
      `/workspace/delete_workspace?workspace_id=${workspaceId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};


export const useUpdateWorkspace = () => {
   return useMutation({
     mutationFn: async (payload) => {
        const response = await axiosInstance.put('/workspace/last_active_workspace_and_project', payload);
        return response?.data
     }
   })
}